import type { ReactNode } from "react";
import { Chip } from "./OptionCard";
import { SectionLabel } from "./Field";

export interface MixedOption<T extends string> {
  id: T;
  label: string;
  /** Options default live; set false to render the entry disabled. */
  live?: boolean;
}

interface OptionRowProps<T extends string> {
  label: string;
  options: MixedOption<T>[];
  selected: T;
  onSelect: (id: T) => void;
  /** Grey out the WHOLE row — the setting is real and the viewer supports it,
   *  but it does not apply to the current state (a bead joint has no groove
   *  floor to finish). Distinct from a per-option `live: false`, which means
   *  the connected viewer cannot honour that one entry. */
  disabled?: boolean;
}

/**
 * Labelled chip row. Entries flagged `live: false` render disabled (a catalog
 * option the connected viewer cannot honour), so live and gated options read as
 * one system.
 */
export function MixedOptionRow<T extends string>({ label, options, selected, onSelect, disabled = false }: OptionRowProps<T>) {
  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>{label}</SectionLabel>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) =>
          opt.live === false ? (
            <span
              key={opt.id}
              title="Not available on this viewer"
              className="cursor-not-allowed rounded-lg border border-outline-variant/40 bg-surface px-3 py-2 font-display text-[10px] font-semibold uppercase tracking-[0.1em] text-on-surface-variant opacity-50"
            >
              {opt.label}
            </span>
          ) : (
            <Chip key={opt.id} selected={selected === opt.id} disabled={disabled} onClick={() => onSelect(opt.id)}>
              {opt.label}
            </Chip>
          ),
        )}
      </div>
    </div>
  );
}

/** All-live chip row — the common case (`MixedOption.live` defaults true). */
export const OptionRow = MixedOptionRow;

/**
 * A whole option group shown but not interactive — the real catalog is visible
 * so nothing looks missing while the RPC is unavailable.
 */
export function DisabledOptionRow({ label, options, selectedIndex = 0 }: {
  label: string;
  options: string[];
  selectedIndex?: number;
}) {
  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>{label}</SectionLabel>
      <div className="flex flex-wrap gap-2 opacity-50">
        {options.map((opt, i) => (
          <span
            key={opt}
            className={`cursor-not-allowed rounded-lg border px-3 py-2 font-display text-[10px] font-semibold uppercase tracking-[0.1em] ${
              i === selectedIndex
                ? "border-secondary bg-secondary-container/20 text-secondary"
                : "border-outline-variant/40 bg-surface text-on-surface-variant"
            }`}
          >
            {opt}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Read-only slider stand-in matching `Slider`'s layout. */
export function DisabledSlider({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-2 opacity-50">
      <div className="flex items-center justify-between">
        <span className="font-display text-[11px] font-medium uppercase tracking-[0.12em] text-on-surface-variant">
          {label}
        </span>
        <span className="font-body text-[13px] tabular-nums text-primary">{value}</span>
      </div>
      <input type="range" className="lux" disabled value={50} min={0} max={100} readOnly />
    </div>
  );
}

/** Wraps a section whose controls are all inert. */
export function DisabledSectionBody({ children }: { children: ReactNode }) {
  return <div className="pointer-events-none flex flex-col gap-5">{children}</div>;
}
